/* eslint-disable max-lines */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Editor } from "@tiptap/react"

import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  GraduationCap,
  Highlighter,
  ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  ListTodo,
  Loader2,
  MoreHorizontal,
  Quote,
  Redo,
  Smile,
  Sparkles,
  Strikethrough,
  Type,
  Underline,
  Undo,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type ReadingLevel =
  | "kindergarten"
  | "elementary"
  | "middle_school"
  | "high_school"
  | "college"
  | "graduate"

type LengthAdjustment = "much_shorter" | "shorter" | "keep" | "longer" | "much_longer"

type EmojiTarget = "words" | "sections" | "lists" | "remove"

type FormatToolbarProps = {
  editor: Editor | null
  disabled?: boolean
  isAiProcessing?: boolean
  onPolish?: () => void
  onAddEmojis?: (target: EmojiTarget) => void
  onReadingLevelChange?: (level: ReadingLevel) => void
  onLengthChange?: (length: LengthAdjustment) => void
  onCustomAiEdit?: (instruction: string) => void
  onImageUpload?: () => void
}

const FONT_FAMILIES = [
  { label: "Sans Serif", value: "Inter, ui-sans-serif, system-ui, sans-serif" },
  { label: "Serif", value: "Georgia, Cambria, serif" },
  { label: "Monospace", value: "ui-monospace, SFMono-Regular, monospace" },
]

const FONT_SIZES = [
  { label: "Small", value: "14px" },
  { label: "Normal", value: "16px" },
  { label: "Large", value: "18px" },
  { label: "X-Large", value: "24px" },
  { label: "XX-Large", value: "32px" },
]

const TEXT_COLORS = [
  { label: "Default", value: "" },
  { label: "Gray", value: "#6b7280" },
  { label: "Red", value: "#dc2626" },
  { label: "Orange", value: "#ea580c" },
  { label: "Yellow", value: "#ca8a04" },
  { label: "Green", value: "#16a34a" },
  { label: "Blue", value: "#2563eb" },
  { label: "Purple", value: "#9333ea" },
  { label: "Pink", value: "#db2777" },
]

const HIGHLIGHT_COLORS = [
  { label: "None", value: "" },
  { label: "Yellow", value: "#fef08a" },
  { label: "Green", value: "#bbf7d0" },
  { label: "Blue", value: "#bfdbfe" },
  { label: "Pink", value: "#fbcfe8" },
  { label: "Purple", value: "#e9d5ff" },
  { label: "Orange", value: "#fed7aa" },
]

const OVERFLOW_EPSILON_PX = 1
const EXPAND_BUFFER_PX = 90
type CollapseStep =
  | "image_upload"
  | "link"
  | "bullet_list"
  | "ordered_list"
  | "task_list"
  | "blockquote"
  | "alignment"
  | "ai_actions"
  | "highlight"
  | "text_color"
  | "font_size"
  | "font_family"

export function FormatToolbar({
  editor,
  disabled = false,
  isAiProcessing = false,
  onPolish,
  onAddEmojis,
  onReadingLevelChange,
  onLengthChange,
  onImageUpload,
}: FormatToolbarProps) {
  const { t } = useTranslation()
  const toolbarViewportRef = useRef<HTMLDivElement>(null)
  const toolbarContentRef = useRef<HTMLDivElement>(null)
  const [linkUrl, setLinkUrl] = useState("")
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false)
  const [collapseLevel, setCollapseLevel] = useState(0)
  const [, forceUpdate] = useState(0)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const linkPopoverRef = useRef<HTMLDivElement>(null)
  const linkSelectionRef = useRef<{ from: number; to: number } | null>(null)
  const lastEditorSelectionRef = useRef<{ from: number; to: number } | null>(null)
  // True after the container drops to zero width (panel hidden / closing).
  // On the first non-zero measurement we reset collapseLevel so the cascade
  // recalculates from scratch instead of staying stuck at the level it reached
  // during the CSS open-animation.
  const wasZeroWidthRef = useRef(false)

  const hasAiActions = Boolean(onPolish || onAddEmojis || onReadingLevelChange || onLengthChange)
  const hasImageUpload = Boolean(onImageUpload)
  const collapseOrder: CollapseStep[] = [
    ...(hasImageUpload ? (["image_upload"] as const) : []),
    "link",
    "bullet_list",
    "ordered_list",
    "task_list",
    "blockquote",
    "alignment",
    ...(hasAiActions ? (["ai_actions"] as const) : []),
    "highlight",
    "text_color",
    "font_size",
    "font_family",
  ]
  const maxCollapseLevel = collapseOrder.length

  useEffect(() => {
    if (!editor) return

    const rememberSelection = () => {
      const { from, to } = editor.state.selection
      lastEditorSelectionRef.current = { from, to }
    }

    rememberSelection()
    editor.on("selectionUpdate", rememberSelection)
    return () => {
      editor.off("selectionUpdate", rememberSelection)
    }
  }, [editor])

  useEffect(() => {
    if (linkPopoverOpen) {
      if (!linkSelectionRef.current) {
        linkSelectionRef.current = lastEditorSelectionRef.current
      }
      const existingHref = editor?.getAttributes("link").href as string | undefined
      setLinkUrl(existingHref ?? "")
      setTimeout(() => linkInputRef.current?.focus(), 0)
      return
    }
    linkSelectionRef.current = null
  }, [linkPopoverOpen, editor])

  useEffect(() => {
    if (!linkPopoverOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (linkPopoverRef.current && !linkPopoverRef.current.contains(event.target as Node)) {
        setLinkPopoverOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [linkPopoverOpen])

  useEffect(() => {
    const handleOpenLinkPopover = () => {
      linkSelectionRef.current = lastEditorSelectionRef.current
      setLinkPopoverOpen(true)
    }
    window.addEventListener("canvas:open-link-popover", handleOpenLinkPopover)
    return () => window.removeEventListener("canvas:open-link-popover", handleOpenLinkPopover)
  }, [])

  const updateCollapseLevel = useCallback(() => {
    const toolbarViewport = toolbarViewportRef.current
    const toolbarContent = toolbarContentRef.current
    if (!toolbarViewport || !toolbarContent) return
    const viewportWidth = toolbarViewport.clientWidth

    if (viewportWidth === 0) {
      wasZeroWidthRef.current = true
      return
    }

    // Container just became visible after being hidden. Clear the flag and fall
    // through to the normal logic so we recalculate from current measurements
    // instead of staying stuck at the level from intermediate animation frames.
    if (wasZeroWidthRef.current) {
      wasZeroWidthRef.current = false
    }

    const overflowAmount = toolbarContent.scrollWidth - viewportWidth

    setCollapseLevel((currentLevel) => {
      const clampedCurrentLevel = Math.min(currentLevel, maxCollapseLevel)

      if (overflowAmount > OVERFLOW_EPSILON_PX) {
        if (clampedCurrentLevel >= maxCollapseLevel) {
          return clampedCurrentLevel
        }
        // Jump multiple levels proportional to overflow so we converge in 1-2 frames
        // rather than one frame per item (average toolbar button ≈ 28 px)
        const jump = Math.max(1, Math.ceil(overflowAmount / 28))
        return Math.min(clampedCurrentLevel + jump, maxCollapseLevel)
      }

      if (clampedCurrentLevel === 0) return clampedCurrentLevel

      const freeSpace = viewportWidth - toolbarContent.scrollWidth
      if (freeSpace > EXPAND_BUFFER_PX) {
        return clampedCurrentLevel - 1
      }

      return clampedCurrentLevel
    })
  }, [maxCollapseLevel])

  // Re-run when editor becomes available so we set up the observer after the
  // toolbar refs are populated (FormatToolbar returns null when editor is null).
  useLayoutEffect(() => {
    if (
      !editor ||
      typeof ResizeObserver === "undefined" ||
      !toolbarViewportRef.current ||
      !toolbarContentRef.current
    ) {
      return
    }

    const toolbarViewport = toolbarViewportRef.current
    const toolbarContent = toolbarContentRef.current
    const observer = new ResizeObserver(() => {
      updateCollapseLevel()
    })

    observer.observe(toolbarViewport)
    observer.observe(toolbarContent)
    if (toolbarViewport.parentElement) {
      observer.observe(toolbarViewport.parentElement)
    }

    return () => {
      observer.disconnect()
    }
  }, [editor, updateCollapseLevel])

  // Call synchronously (no RAF) so the entire collapse cascade runs before the
  // first browser paint. State updates from useLayoutEffect are flushed by React
  // before painting, which prevents the toolbar from ever appearing clipped.
  useLayoutEffect(() => {
    updateCollapseLevel()
  }, [updateCollapseLevel, hasAiActions, hasImageUpload, isAiProcessing])

  useEffect(() => {
    if (!editor) return

    const handleUpdate = () => forceUpdate((count) => count + 1)

    editor.on("selectionUpdate", handleUpdate)
    editor.on("transaction", handleUpdate)

    return () => {
      editor.off("selectionUpdate", handleUpdate)
      editor.off("transaction", handleUpdate)
    }
  }, [editor])

  const getCurrentFontFamily = useCallback(() => {
    if (!editor) return FONT_FAMILIES[0].label
    const attrs = editor.getAttributes("textStyle")
    const currentFont = attrs.fontFamily as string | undefined
    const match = FONT_FAMILIES.find((font) => font.value === currentFont)
    return match?.label ?? "Sans Serif"
  }, [editor])

  const getCurrentFontSize = useCallback(() => {
    if (!editor) return "Normal"
    const attrs = editor.getAttributes("textStyle")
    const currentSize = attrs.fontSize as string | undefined
    const match = FONT_SIZES.find((size) => size.value === currentSize)
    return match?.label ?? "Normal"
  }, [editor])

  const getCurrentHeadingLabel = useCallback(() => {
    if (!editor) return t("canvas.paragraph", "Paragraph")
    if (editor.isActive("heading", { level: 1 })) return "H1"
    if (editor.isActive("heading", { level: 2 })) return "H2"
    if (editor.isActive("heading", { level: 3 })) return "H3"
    return t("canvas.paragraph", "Paragraph")
  }, [editor, t])

  const normalizeLinkUrl = useCallback((value: string) => {
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return value
    }
    return `https://${value}`
  }, [])

  const getShortLinkLabel = useCallback((url: string) => {
    try {
      const hostname = new URL(url).hostname.replace(/^www\./, "")
      return hostname || url
    } catch {
      const trimmed = url.replace(/^https?:\/\//, "")
      const [host] = trimmed.split("/")
      return host || trimmed || url
    }
  }, [])

  const handleSetLink = useCallback(() => {
    if (!editor) return
    const trimmedUrl = linkUrl.trim()
    if (!trimmedUrl) return

    const url = normalizeLinkUrl(trimmedUrl)
    const fallbackSelection = editor.state.selection
    const targetSelection = linkSelectionRef.current ?? {
      from: fallbackSelection.from,
      to: fallbackSelection.to,
    }

    const chain = editor.chain().focus().setTextSelection(targetSelection)
    const hasSelectedText = targetSelection.from !== targetSelection.to
    const hasActiveLink = editor.isActive("link")

    if (!hasSelectedText && !hasActiveLink) {
      const label = getShortLinkLabel(url)
      chain
        .insertContent(label)
        .setTextSelection({
          from: targetSelection.from,
          to: targetSelection.from + label.length,
        })
        .setLink({ href: url })
        .run()
    } else {
      chain.extendMarkRange("link").setLink({ href: url }).run()
    }

    linkSelectionRef.current = null
    setLinkUrl("")
    setLinkPopoverOpen(false)
  }, [editor, getShortLinkLabel, linkUrl, normalizeLinkUrl])

  const handleRemoveLink = useCallback(() => {
    if (!editor) return
    const fallbackSelection = editor.state.selection
    const targetSelection = linkSelectionRef.current ?? {
      from: fallbackSelection.from,
      to: fallbackSelection.to,
    }
    editor.chain().focus().setTextSelection(targetSelection).unsetLink().run()
    linkSelectionRef.current = null
    setLinkPopoverOpen(false)
  }, [editor])

  const getCurrentAlignmentIcon = useCallback(() => {
    if (!editor) return AlignLeft
    if (editor.isActive({ textAlign: "center" })) return AlignCenter
    if (editor.isActive({ textAlign: "right" })) return AlignRight
    if (editor.isActive({ textAlign: "justify" })) return AlignJustify
    return AlignLeft
  }, [editor])

  if (!editor) return null

  const CurrentAlignIcon = getCurrentAlignmentIcon()
  const collapsedSteps = new Set(collapseOrder.slice(0, Math.min(collapseLevel, maxCollapseLevel)))
  const collapseImageUpload = collapsedSteps.has("image_upload")
  const collapseLink = collapsedSteps.has("link")
  const collapseBulletList = collapsedSteps.has("bullet_list")
  const collapseOrderedList = collapsedSteps.has("ordered_list")
  const collapseTaskList = collapsedSteps.has("task_list")
  const collapseBlockquote = collapsedSteps.has("blockquote")
  const collapseAlignment = collapsedSteps.has("alignment")
  const collapseAiActions = collapsedSteps.has("ai_actions")
  const collapseHighlight = collapsedSteps.has("highlight")
  const collapseTextColor = collapsedSteps.has("text_color")
  const collapseFontSize = collapsedSteps.has("font_size")
  const collapseFontFamily = collapsedSteps.has("font_family")
  const collapseStructureTools =
    collapseBulletList || collapseOrderedList || collapseTaskList || collapseBlockquote
  const hasCollapsedAiActions = collapseAiActions
  const hasCollapsedImageUpload = collapseImageUpload
  const hasCollapsedLink = collapseLink
  const hasCollapsedAlignment = collapseAlignment
  const hasCollapsedStructureTools = collapseStructureTools
  const hasCollapsedHighlight = collapseHighlight
  const hasCollapsedTextColor = collapseTextColor
  const hasCollapsedFontSize = collapseFontSize
  const hasCollapsedFontFamily = collapseFontFamily
  const hasOverflowMenu =
    hasCollapsedAiActions ||
    hasCollapsedImageUpload ||
    hasCollapsedLink ||
    hasCollapsedAlignment ||
    hasCollapsedStructureTools ||
    hasCollapsedHighlight ||
    hasCollapsedTextColor ||
    hasCollapsedFontSize ||
    hasCollapsedFontFamily

  const renderAiActionMenuItems = () => (
    <>
      {onPolish && (
        <DropdownMenuItem onClick={onPolish}>
          <Sparkles className="mr-2 size-4" />
          {t("canvas.ai_polish", { defaultValue: "Polish text" })}
        </DropdownMenuItem>
      )}
      {onAddEmojis && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Smile className="mr-2 size-4" />
            {t("canvas.ai_add_emojis", { defaultValue: "Add emojis" })}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => onAddEmojis("words")}>
              {t("canvas.ai_emojis_words", { defaultValue: "To key words" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddEmojis("sections")}>
              {t("canvas.ai_emojis_sections", { defaultValue: "To sections" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddEmojis("lists")}>
              {t("canvas.ai_emojis_lists", { defaultValue: "To list items" })}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAddEmojis("remove")}>
              {t("canvas.ai_emojis_remove", { defaultValue: "Remove emojis" })}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {onReadingLevelChange && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <GraduationCap className="mr-2 size-4" />
            {t("canvas.ai_reading_level", { defaultValue: "Adjust reading level" })}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => onReadingLevelChange("kindergarten")}>
              {t("canvas.ai_level_kindergarten", { defaultValue: "Kindergarten" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onReadingLevelChange("elementary")}>
              {t("canvas.ai_level_elementary", { defaultValue: "Elementary" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onReadingLevelChange("middle_school")}>
              {t("canvas.ai_level_middle_school", { defaultValue: "Middle school" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onReadingLevelChange("high_school")}>
              {t("canvas.ai_level_high_school", { defaultValue: "High school" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onReadingLevelChange("college")}>
              {t("canvas.ai_level_college", { defaultValue: "College" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onReadingLevelChange("graduate")}>
              {t("canvas.ai_level_graduate", { defaultValue: "Graduate" })}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {onLengthChange && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Type className="mr-2 size-4" />
            {t("canvas.ai_adjust_length", { defaultValue: "Adjust length" })}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => onLengthChange("much_shorter")}>
              {t("canvas.ai_length_much_shorter", { defaultValue: "Much shorter" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onLengthChange("shorter")}>
              {t("canvas.ai_length_shorter", { defaultValue: "Shorter" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onLengthChange("longer")}>
              {t("canvas.ai_length_longer", { defaultValue: "Longer" })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onLengthChange("much_longer")}>
              {t("canvas.ai_length_much_longer", { defaultValue: "Much longer" })}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
    </>
  )

  return (
    <div
      ref={toolbarViewportRef}
      className="mx-auto flex w-full max-w-full min-w-0 justify-center overflow-hidden"
    >
      <div
        ref={toolbarContentRef}
        className={cn(
          "dark:bg-background inline-flex w-max max-w-none flex-nowrap items-center justify-start gap-0.5 rounded-lg border bg-white px-3 py-1.5 shadow-[0_2px_12px_rgba(0,0,0,0.08)] *:shrink-0",
          disabled && "[&_button:disabled]:opacity-100"
        )}
      >
        {/* Undo/Redo */}
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-7"
                disabled={disabled || !editor.can().undo()}
                size="icon"
                variant="ghost"
                onClick={() => editor.chain().focus().undo().run()}
              >
                <Undo className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("canvas.undo", "Undo")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-7"
                disabled={disabled || !editor.can().redo()}
                size="icon"
                variant="ghost"
                onClick={() => editor.chain().focus().redo().run()}
              >
                <Redo className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("canvas.redo", "Redo")}</TooltipContent>
          </Tooltip>
        </div>

        <div className="bg-border mx-0.5 hidden h-5 w-px sm:block" />

        {/* Heading Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="h-7 gap-0.5 px-2 text-xs" disabled={disabled} variant="ghost">
              {getCurrentHeadingLabel()}
              <ChevronDown className="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => editor.chain().focus().setParagraph().run()}>
              <span className="text-sm">{t("canvas.paragraph", "Paragraph")}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            >
              <span className="text-xl font-bold">Heading 1</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            >
              <span className="text-lg font-bold">Heading 2</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            >
              <span className="text-base font-bold">Heading 3</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {(!collapseFontFamily || !collapseFontSize) && (
          <div className="bg-border mx-0.5 hidden h-5 w-px sm:block" />
        )}

        {/* Font Family */}
        {!collapseFontFamily && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="h-7 w-[86px] justify-between gap-0.5 px-2 text-xs"
                disabled={disabled}
                variant="ghost"
              >
                <span className="truncate">{getCurrentFontFamily()}</span>
                <ChevronDown className="size-3 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {FONT_FAMILIES.map((font) => (
                <DropdownMenuItem
                  key={font.value}
                  style={{ fontFamily: font.value }}
                  onClick={() => editor.chain().focus().setFontFamily(font.value).run()}
                >
                  {font.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Font Size */}
        {!collapseFontSize && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="h-7 w-[70px] justify-between gap-0.5 px-2 text-xs"
                disabled={disabled}
                variant="ghost"
              >
                <span className="truncate">{getCurrentFontSize()}</span>
                <ChevronDown className="size-3 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {FONT_SIZES.map((size) => (
                <DropdownMenuItem
                  key={size.value}
                  style={{ fontSize: size.value }}
                  onClick={() => editor.chain().focus().setFontSize(size.value).run()}
                >
                  {size.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="bg-border mx-0.5 hidden h-5 w-px sm:block" />

        {/* Bold */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              disabled={disabled}
              size="icon"
              variant="ghost"
              className={cn(
                "size-7",
                editor.isActive("bold") &&
                  "border-foreground bg-accent text-accent-foreground border"
              )}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <Bold className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("canvas.bold", "Bold")} (⌘B)</TooltipContent>
        </Tooltip>

        {/* Italic */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              disabled={disabled}
              size="icon"
              variant="ghost"
              className={cn(
                "size-7",
                editor.isActive("italic") &&
                  "border-foreground bg-accent text-accent-foreground border"
              )}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <Italic className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("canvas.italic", "Italic")} (⌘I)</TooltipContent>
        </Tooltip>

        {/* Underline */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              disabled={disabled}
              size="icon"
              variant="ghost"
              className={cn(
                "size-7",
                editor.isActive("underline") &&
                  "border-foreground bg-accent text-accent-foreground border"
              )}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <Underline className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("canvas.underline", "Underline")} (⌘U)</TooltipContent>
        </Tooltip>

        {/* Strikethrough */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              disabled={disabled}
              size="icon"
              variant="ghost"
              className={cn(
                "size-7",
                editor.isActive("strike") &&
                  "border-foreground bg-accent text-accent-foreground border"
              )}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <Strikethrough className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("canvas.strikethrough", "Strikethrough")}</TooltipContent>
        </Tooltip>

        <div className="bg-border mx-0.5 hidden h-5 w-px sm:block" />

        {/* Text Color */}
        {!collapseTextColor && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="relative size-7"
                disabled={disabled}
                size="icon"
                title={t("canvas.text_color", "Text color")}
                variant="ghost"
              >
                <span
                  className="text-sm font-bold"
                  style={{
                    color: (editor.getAttributes("textStyle").color as string) || "currentColor",
                  }}
                >
                  A
                </span>
                <span
                  className="absolute bottom-1.5 h-0.5 w-3 rounded-full"
                  style={{
                    backgroundColor:
                      (editor.getAttributes("textStyle").color as string) || "currentColor",
                  }}
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              {TEXT_COLORS.map((color) => (
                <DropdownMenuItem
                  key={color.value || "default"}
                  onClick={() =>
                    color.value
                      ? editor.chain().focus().setColor(color.value).run()
                      : editor.chain().focus().unsetColor().run()
                  }
                >
                  <span
                    className="border-border mr-2 size-4 rounded-full border"
                    style={{ backgroundColor: color.value || "transparent" }}
                  />
                  {color.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Highlight */}
        {!collapseHighlight && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="size-7"
                disabled={disabled}
                size="icon"
                title={t("canvas.highlight", "Highlight")}
                variant="ghost"
              >
                <Highlighter className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              {HIGHLIGHT_COLORS.map((color) => (
                <DropdownMenuItem
                  key={color.value || "none"}
                  onClick={() =>
                    color.value
                      ? editor.chain().focus().toggleHighlight({ color: color.value }).run()
                      : editor.chain().focus().unsetHighlight().run()
                  }
                >
                  <span
                    className="border-border mr-2 size-4 rounded border"
                    style={{ backgroundColor: color.value || "transparent" }}
                  />
                  {color.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* AI Actions */}
        {hasAiActions && !collapseAiActions && (
          <>
            <div className="bg-border mx-0.5 hidden h-5 w-px sm:block" />
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="size-7"
                      disabled={disabled || isAiProcessing}
                      size="icon"
                      variant="ghost"
                    >
                      {isAiProcessing ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Sparkles className="size-4" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  {t("canvas.ai_actions", { defaultValue: "AI Actions" })}
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="start" className="w-56">
                {renderAiActionMenuItems()}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}

        {(!collapseAlignment || !collapseStructureTools) && (
          <div className="bg-border mx-0.5 hidden h-5 w-px sm:block" />
        )}

        {/* Alignment Dropdown */}
        {!collapseAlignment && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button className="size-7" disabled={disabled} size="icon" variant="ghost">
                    <CurrentAlignIcon className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t("canvas.alignment", "Alignment")}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                className={cn(
                  editor.isActive({ textAlign: "left" }) &&
                    "border-foreground bg-accent text-accent-foreground border"
                )}
                onClick={() => editor.chain().focus().setTextAlign("left").run()}
              >
                <AlignLeft className="mr-2 size-4" />
                {t("canvas.align_left", "Align left")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className={cn(
                  editor.isActive({ textAlign: "center" }) &&
                    "border-foreground bg-accent text-accent-foreground border"
                )}
                onClick={() => editor.chain().focus().setTextAlign("center").run()}
              >
                <AlignCenter className="mr-2 size-4" />
                {t("canvas.align_center", "Align center")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className={cn(
                  editor.isActive({ textAlign: "right" }) &&
                    "border-foreground bg-accent text-accent-foreground border"
                )}
                onClick={() => editor.chain().focus().setTextAlign("right").run()}
              >
                <AlignRight className="mr-2 size-4" />
                {t("canvas.align_right", "Align right")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className={cn(
                  editor.isActive({ textAlign: "justify" }) &&
                    "border-foreground bg-accent text-accent-foreground border"
                )}
                onClick={() => editor.chain().focus().setTextAlign("justify").run()}
              >
                <AlignJustify className="mr-2 size-4" />
                {t("canvas.align_justify", "Justify")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {!collapseAlignment &&
          (!collapseBulletList ||
            !collapseOrderedList ||
            !collapseTaskList ||
            !collapseBlockquote) && <div className="bg-border mx-0.5 hidden h-5 w-px sm:block" />}

        {/* Lists */}
        {!collapseBulletList && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={disabled}
                size="icon"
                variant="ghost"
                className={cn(
                  "size-7",
                  editor.isActive("bulletList") &&
                    "border-foreground bg-accent text-accent-foreground border"
                )}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                <List className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("canvas.bullet_list", "Bullet list")}</TooltipContent>
          </Tooltip>
        )}
        {!collapseOrderedList && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={disabled}
                size="icon"
                variant="ghost"
                className={cn(
                  "size-7",
                  editor.isActive("orderedList") &&
                    "border-foreground bg-accent text-accent-foreground border"
                )}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              >
                <ListOrdered className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("canvas.ordered_list", "Numbered list")}</TooltipContent>
          </Tooltip>
        )}
        {!collapseTaskList && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={disabled}
                size="icon"
                variant="ghost"
                className={cn(
                  "size-7",
                  editor.isActive("taskList") &&
                    "border-foreground bg-accent text-accent-foreground border"
                )}
                onClick={() => editor.chain().focus().toggleTaskList().run()}
              >
                <ListTodo className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("canvas.task_list", "Checklist")}</TooltipContent>
          </Tooltip>
        )}
        {!collapseBlockquote && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                disabled={disabled}
                size="icon"
                variant="ghost"
                className={cn(
                  "size-7",
                  editor.isActive("blockquote") &&
                    "border-foreground bg-accent text-accent-foreground border"
                )}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
              >
                <Quote className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("canvas.blockquote", "Quote")}</TooltipContent>
          </Tooltip>
        )}

        {/* Link */}
        <div ref={linkPopoverRef} className="relative">
          {!collapseLink && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  disabled={disabled}
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "size-7",
                    editor.isActive("link") &&
                      "border-foreground bg-accent text-accent-foreground border"
                  )}
                  onClick={() => {
                    setLinkPopoverOpen((open) => !open)
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    linkSelectionRef.current = lastEditorSelectionRef.current
                  }}
                >
                  <LinkIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("canvas.link", "Insert link")} (⌘⇧K)</TooltipContent>
            </Tooltip>
          )}
          {linkPopoverOpen && (
            <div className="bg-popover text-popover-foreground absolute top-full right-0 z-50 mt-1 w-64 rounded-md border p-3 shadow-md">
              <div className="flex flex-col gap-2">
                <label className="text-foreground text-sm font-medium">
                  {t("canvas.link_url", "URL")}
                </label>
                <Input
                  ref={linkInputRef}
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(event) => setLinkUrl(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      handleSetLink()
                    }
                    if (event.key === "Escape") {
                      setLinkPopoverOpen(false)
                    }
                  }}
                />
                <div className="flex gap-2">
                  <Button className="flex-1" size="sm" onClick={handleSetLink}>
                    {editor.isActive("link")
                      ? t("canvas.update_link", "Update")
                      : t("canvas.insert_link", "Insert")}
                  </Button>
                  {editor.isActive("link") && (
                    <Button size="sm" variant="outline" onClick={handleRemoveLink}>
                      {t("canvas.remove_link", "Remove")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Image Upload */}
        {onImageUpload && !collapseImageUpload && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-7"
                disabled={disabled}
                size="icon"
                variant="ghost"
                onClick={onImageUpload}
              >
                <ImageIcon className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {t("canvas.insert_image", { defaultValue: "Insert image" })}
            </TooltipContent>
          </Tooltip>
        )}

        {hasOverflowMenu && (
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label={t("canvas.more_tools", { defaultValue: "More tools" })}
                    className="size-7"
                    disabled={disabled}
                    size="icon"
                    variant="ghost"
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>
                {t("canvas.more_tools", { defaultValue: "More tools" })}
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-56">
              {hasCollapsedAlignment && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <CurrentAlignIcon className="mr-2 size-4" />
                    {t("canvas.alignment", "Alignment")}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setTextAlign("left").run()}
                    >
                      <AlignLeft className="mr-2 size-4" />
                      {t("canvas.align_left", "Align left")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setTextAlign("center").run()}
                    >
                      <AlignCenter className="mr-2 size-4" />
                      {t("canvas.align_center", "Align center")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setTextAlign("right").run()}
                    >
                      <AlignRight className="mr-2 size-4" />
                      {t("canvas.align_right", "Align right")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().setTextAlign("justify").run()}
                    >
                      <AlignJustify className="mr-2 size-4" />
                      {t("canvas.align_justify", "Justify")}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              {hasCollapsedStructureTools && (
                <>
                  {hasCollapsedAlignment && <DropdownMenuSeparator />}
                  {collapseBulletList && (
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                    >
                      <List className="mr-2 size-4" />
                      {t("canvas.bullet_list", "Bullet list")}
                    </DropdownMenuItem>
                  )}
                  {collapseOrderedList && (
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    >
                      <ListOrdered className="mr-2 size-4" />
                      {t("canvas.ordered_list", "Numbered list")}
                    </DropdownMenuItem>
                  )}
                  {collapseTaskList && (
                    <DropdownMenuItem onClick={() => editor.chain().focus().toggleTaskList().run()}>
                      <ListTodo className="mr-2 size-4" />
                      {t("canvas.task_list", "Checklist")}
                    </DropdownMenuItem>
                  )}
                  {collapseBlockquote && (
                    <DropdownMenuItem
                      onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    >
                      <Quote className="mr-2 size-4" />
                      {t("canvas.blockquote", "Quote")}
                    </DropdownMenuItem>
                  )}
                </>
              )}

              {hasCollapsedLink && (
                <>
                  {(hasCollapsedAlignment || hasCollapsedStructureTools) && (
                    <DropdownMenuSeparator />
                  )}
                  <DropdownMenuItem
                    onSelect={() => {
                      linkSelectionRef.current = lastEditorSelectionRef.current
                      setLinkPopoverOpen(true)
                    }}
                  >
                    <LinkIcon className="mr-2 size-4" />
                    {t("canvas.insert_link", "Insert link")}
                  </DropdownMenuItem>
                </>
              )}

              {hasCollapsedImageUpload && (
                <>
                  {(hasCollapsedAlignment || hasCollapsedStructureTools || hasCollapsedLink) && (
                    <DropdownMenuSeparator />
                  )}
                  <DropdownMenuItem onClick={onImageUpload}>
                    <ImageIcon className="mr-2 size-4" />
                    {t("canvas.insert_image", { defaultValue: "Insert image" })}
                  </DropdownMenuItem>
                </>
              )}

              {hasCollapsedAiActions && (
                <>
                  {(hasCollapsedAlignment ||
                    hasCollapsedStructureTools ||
                    hasCollapsedLink ||
                    hasCollapsedImageUpload) && <DropdownMenuSeparator />}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      {isAiProcessing ? (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      ) : (
                        <Sparkles className="mr-2 size-4" />
                      )}
                      {t("canvas.ai_actions", { defaultValue: "AI Actions" })}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-56">
                      {renderAiActionMenuItems()}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}

              {hasCollapsedHighlight && (
                <>
                  {(hasCollapsedAlignment ||
                    hasCollapsedStructureTools ||
                    hasCollapsedLink ||
                    hasCollapsedImageUpload ||
                    hasCollapsedAiActions) && <DropdownMenuSeparator />}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Highlighter className="mr-2 size-4" />
                      {t("canvas.highlight", "Highlight")}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-40">
                      {HIGHLIGHT_COLORS.map((color) => (
                        <DropdownMenuItem
                          key={color.value || "none"}
                          onClick={() =>
                            color.value
                              ? editor.chain().focus().toggleHighlight({ color: color.value }).run()
                              : editor.chain().focus().unsetHighlight().run()
                          }
                        >
                          <span
                            className="border-border mr-2 size-4 rounded border"
                            style={{ backgroundColor: color.value || "transparent" }}
                          />
                          {color.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}

              {hasCollapsedTextColor && (
                <>
                  {(hasCollapsedAlignment ||
                    hasCollapsedStructureTools ||
                    hasCollapsedLink ||
                    hasCollapsedImageUpload ||
                    hasCollapsedAiActions ||
                    hasCollapsedHighlight) && <DropdownMenuSeparator />}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <span
                        className="mr-2 text-sm font-bold"
                        style={{
                          color:
                            (editor.getAttributes("textStyle").color as string) || "currentColor",
                        }}
                      >
                        A
                      </span>
                      {t("canvas.text_color", "Text color")}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-40">
                      {TEXT_COLORS.map((color) => (
                        <DropdownMenuItem
                          key={color.value || "default"}
                          onClick={() =>
                            color.value
                              ? editor.chain().focus().setColor(color.value).run()
                              : editor.chain().focus().unsetColor().run()
                          }
                        >
                          <span
                            className="border-border mr-2 size-4 rounded-full border"
                            style={{ backgroundColor: color.value || "transparent" }}
                          />
                          {color.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}

              {hasCollapsedFontSize && (
                <>
                  {(hasCollapsedAlignment ||
                    hasCollapsedStructureTools ||
                    hasCollapsedLink ||
                    hasCollapsedImageUpload ||
                    hasCollapsedAiActions ||
                    hasCollapsedHighlight ||
                    hasCollapsedTextColor) && <DropdownMenuSeparator />}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Type className="mr-2 size-4" />
                      {t("canvas.font_size", { defaultValue: "Font size" })}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {FONT_SIZES.map((size) => (
                        <DropdownMenuItem
                          key={size.value}
                          style={{ fontSize: size.value }}
                          onClick={() => editor.chain().focus().setFontSize(size.value).run()}
                        >
                          {size.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}

              {hasCollapsedFontFamily && (
                <>
                  {(hasCollapsedAlignment ||
                    hasCollapsedStructureTools ||
                    hasCollapsedLink ||
                    hasCollapsedImageUpload ||
                    hasCollapsedAiActions ||
                    hasCollapsedHighlight ||
                    hasCollapsedTextColor ||
                    hasCollapsedFontSize) && <DropdownMenuSeparator />}
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Type className="mr-2 size-4" />
                      {t("canvas.font_family", { defaultValue: "Font" })}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {FONT_FAMILIES.map((font) => (
                        <DropdownMenuItem
                          key={font.value}
                          style={{ fontFamily: font.value }}
                          onClick={() => editor.chain().focus().setFontFamily(font.value).run()}
                        >
                          {font.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
